import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { AuroraBackground } from '../../components/Aurora/AuroraBackground';
import { Magnetic } from '../../components/Hero/Magnetic';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Loader2, Mail, Lock, User, ArrowRight, Eye, EyeOff, ArrowLeft, ShieldCheck, Sparkles } from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

const serifStyle = { fontFamily: '"Instrument Serif"' };

/* ════════════════════════ Shared shell ════════════════════════ */
function AuthShell({ children, eyebrow, title, italicAccent, subtitle, testid }) {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]" data-testid={testid}>
      <Navbar />

      <main className="flex-1 relative overflow-hidden isolate">
        {/* Dark: animated aurora.  Light: cobalt mesh + grid. */}
        <div className="hidden dark:block"><AuroraBackground intensity={0.85} /></div>
        <div className="absolute inset-0 dark:hidden pointer-events-none" style={{
          background:
            'radial-gradient(800px 400px at 20% 0%, hsl(226 100% 55% / 0.10), transparent 60%),' +
            'radial-gradient(900px 500px at 95% 10%, hsl(270 95% 60% / 0.06), transparent 60%),' +
            'radial-gradient(700px 500px at 50% 100%, hsl(150 80% 55% / 0.05), transparent 60%)',
        }} />
        <div className="absolute inset-0 dark:hidden opacity-[0.5] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(hsl(220 16% 92%) 1px, transparent 1px), linear-gradient(90deg, hsl(220 16% 92%) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
        }} />

        <div className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 py-12 lg:py-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center min-h-[calc(100vh-160px)]">
          {/* LEFT: editorial copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block max-w-xl"
          >
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-5">◢ {eyebrow}</div>
            <h1 className="font-bold tracking-[-0.04em] leading-[0.95]" style={{ fontSize: 'clamp(2.4rem, 4.4vw, 4rem)' }}>
              <span className="block">{title}</span>
              <span className="block italic font-normal text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] dark:glow-cobalt" style={serifStyle}>{italicAccent}</span>
            </h1>
            <p className="mt-6 max-w-md text-base text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">{subtitle}</p>

            <div className="mt-10 flex flex-col gap-4">
              {[
                { i: ShieldCheck, t: 'Verified credentials',  s: 'Licensed · Bonded · Insured — vetted before listing.' },
                { i: Sparkles,    t: 'Servio Score™ trust',   s: '5-dimensional rating. No pay-to-rank. Ever.' },
                { i: Mail,        t: 'Instant client quotes', s: 'Receive structured leads the second they land.' },
              ].map(({ i: Icon, t, s }, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + idx * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="h-9 w-9 shrink-0 rounded-xl border border-slate-200 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] flex items-center justify-center">
                    <Icon className="h-4 w-4 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-semibold tracking-tight text-sm">{t}</div>
                    <div className="text-sm text-slate-500 dark:text-[hsl(220,12%,65%)]">{s}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT: glass card with form */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md mx-auto lg:mx-0"
          >
            <div className="relative rounded-3xl border border-slate-200 dark:border-white/10 bg-white/90 dark:bg-[hsl(220,30%,8%)]/85 backdrop-blur-xl p-7 sm:p-8 shadow-[0_24px_60px_-16px_rgba(15,23,42,0.18)] dark:shadow-[0_24px_60px_-16px_rgba(30,91,246,0.45)]">
              <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[hsl(226,100%,55%)]/60 to-transparent" />
              {children}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ════════════════════════ Inputs ════════════════════════ */
const inputClasses =
  'w-full h-11 pl-10 pr-3 rounded-xl bg-white dark:bg-[hsl(220,30%,6%)] border border-slate-200 dark:border-white/10 ' +
  'text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-[hsl(220,12%,45%)] ' +
  'focus:outline-none focus:border-[hsl(226,100%,55%)] focus:ring-2 focus:ring-[hsl(226,100%,55%)]/20 ' +
  'transition-colors text-sm';

function LabelRow({ children }) {
  return <div className="mb-1.5 text-xs font-mono tracking-[0.18em] uppercase text-slate-500 dark:text-[hsl(220,12%,60%)]">{children}</div>;
}

function FieldWrap({ icon: Icon, children }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-[hsl(220,12%,50%)]" />
      {children}
    </div>
  );
}

function PasswordField({ id, value, onChange, placeholder, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <FieldWrap icon={Lock}>
      <input
        id={id}
        name={id}
        type={show ? 'text' : 'password'}
        required
        placeholder={placeholder}
        className={inputClasses + ' pr-10'}
        value={value}
        onChange={onChange}
        data-testid={testId}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[hsl(220,12%,55%)] hover:text-slate-700 dark:hover:text-white transition-colors"
        data-testid={`${testId}-toggle`}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </FieldWrap>
  );
}

function PrimaryButton({ children, loading, testid }) {
  return (
    <Magnetic strength={8} block>
      <button
        type="submit"
        disabled={loading}
        className="w-full h-12 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold tracking-tight text-sm shadow-[0_12px_36px_-10px_hsl(226,100%,55%)] transition-colors inline-flex items-center justify-center gap-2"
        data-testid={testid}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
      </button>
    </Magnetic>
  );
}

/* ════════════════════════ Login ════════════════════════ */
export const LoginPage = () => {
  useCanonical('https://servio.legal/login');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      toast.info('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <AuthShell
      testid="login-page"
      eyebrow="WELCOME BACK"
      title="Sign in to your"
      italicAccent="legal directory."
      subtitle="Manage your coverage, quotes, invoices, and Servio Score from one calm, fast control room."
    >
      <Helmet>
        <title>Log In | Servio · Process Server Directory</title>
        <meta name="description" content="Log in to your Servio account to manage your process server profile, view quote requests, and access your dashboard." />
        <link rel="canonical" href="https://servio.legal/login" />
      </Helmet>

      <div className="mb-7" data-testid="login-card">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-2">◢ SIGN IN</div>
        <h2 className="text-2xl font-bold tracking-[-0.02em]">Welcome back.</h2>
        <p className="text-sm text-slate-500 dark:text-[hsl(220,12%,65%)] mt-1">Enter your details to continue.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <LabelRow>Email</LabelRow>
          <FieldWrap icon={Mail}>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="you@firm.legal"
              className={inputClasses}
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              data-testid="login-email"
              autoComplete="email"
            />
          </FieldWrap>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-mono tracking-[0.18em] uppercase text-slate-500 dark:text-[hsl(220,12%,60%)]">Password</span>
            <Link to="/forgot-password" className="text-[11px] font-mono tracking-[0.14em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] hover:underline" data-testid="forgot-password-link">
              Forgot?
            </Link>
          </div>
          <PasswordField
            id="password"
            value={form.password}
            onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder="Enter your password"
            testId="login-password"
            autoComplete="current-password"
          />
        </div>

        <PrimaryButton loading={loading} testid="login-submit">
          Sign In <ArrowRight className="h-4 w-4" />
        </PrimaryButton>
      </form>

      <div className="mt-6 pt-6 border-t border-slate-200 dark:border-white/10 text-center text-sm">
        <span className="text-slate-500 dark:text-[hsl(220,12%,60%)]">New to Servio? </span>
        <Link to="/register" className="text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] font-semibold hover:underline" data-testid="register-link">
          Create a free account
        </Link>
      </div>
    </AuthShell>
  );
};

/* ════════════════════════ Register ════════════════════════ */
export const RegisterPage = () => {
  useCanonical('https://servio.legal/register');
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    fullName: '', userType: 'server', agreedToTerms: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agreedToTerms) { toast.error('Please agree to the Terms of Service and Privacy Policy'); return; }
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, form.fullName, form.userType);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
    setLoading(false);
  };

  return (
    <AuthShell
      testid="register-page"
      eyebrow="GET LISTED"
      title="The directory legal"
      italicAccent="actually trusts."
      subtitle="Founding seats lock in $13.99/mo or $139/yr for life — unlimited coverage, statewide visibility, full toolkit."
    >
      <Helmet>
        <title>Create Account | Servio · Process Server Directory</title>
        <meta name="description" content="Join Servio as a process server or client. Create your free account to get listed, receive quote requests, and grow your business." />
        <link rel="canonical" href="https://servio.legal/register" />
      </Helmet>

      <div className="mb-6" data-testid="register-card">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-2">◢ CREATE ACCOUNT</div>
        <h2 className="text-2xl font-bold tracking-[-0.02em]">Get started in 60 seconds.</h2>
        <p className="text-sm text-slate-500 dark:text-[hsl(220,12%,65%)] mt-1">Free profile · No card required.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <LabelRow>Full Name</LabelRow>
          <FieldWrap icon={User}>
            <input
              id="fullName"
              name="fullName"
              required
              placeholder="John Smith"
              className={inputClasses}
              value={form.fullName}
              onChange={(e) => setForm(f => ({ ...f, fullName: e.target.value }))}
              data-testid="register-name"
              autoComplete="name"
            />
          </FieldWrap>
        </div>

        <div>
          <LabelRow>Email</LabelRow>
          <FieldWrap icon={Mail}>
            <input
              id="register-email"
              name="email"
              type="email"
              required
              placeholder="you@firm.legal"
              className={inputClasses}
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              data-testid="register-email"
              autoComplete="email"
            />
          </FieldWrap>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <LabelRow>Password</LabelRow>
            <PasswordField
              id="password"
              value={form.password}
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Min 8 chars"
              testId="register-password"
              autoComplete="new-password"
            />
          </div>
          <div>
            <LabelRow>Confirm</LabelRow>
            <PasswordField
              id="confirmPassword"
              value={form.confirmPassword}
              onChange={(e) => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
              placeholder="Repeat password"
              testId="register-confirm-password"
              autoComplete="new-password"
            />
          </div>
        </div>

        {/* User type toggle */}
        <div>
          <LabelRow>I am a…</LabelRow>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'server', label: 'Process Server', sub: 'List in directory' },
              { v: 'client', label: 'Client',         sub: 'Hire & track jobs' },
            ].map(({ v, label, sub }) => (
              <button
                key={v}
                type="button"
                onClick={() => setForm(f => ({ ...f, userType: v }))}
                aria-pressed={form.userType === v}
                data-state={form.userType === v ? 'on' : 'off'}
                className={
                  'text-left rounded-xl border px-3 py-2.5 transition-colors ' +
                  (form.userType === v
                    ? 'border-[hsl(226,100%,55%)] bg-[hsl(226,100%,55%)]/10 dark:bg-[hsl(226,100%,55%)]/15'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20')
                }
                data-testid={`usertype-${v}`}
              >
                <div className="text-sm font-semibold tracking-tight">{label}</div>
                <div className="text-[11px] text-slate-500 dark:text-[hsl(220,12%,60%)] mt-0.5">{sub}</div>
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.02] p-3 cursor-pointer">
          <input
            type="checkbox"
            id="agreedToTerms"
            checked={form.agreedToTerms}
            onChange={(e) => setForm(f => ({ ...f, agreedToTerms: e.target.checked }))}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-white/20 text-[hsl(226,100%,55%)] focus:ring-[hsl(226,100%,55%)]"
            data-testid="terms-checkbox"
          />
          <span className="text-[12px] leading-relaxed text-slate-600 dark:text-[hsl(220,12%,70%)]">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" target="_blank" className="text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] hover:underline">Privacy Policy</Link>.
          </span>
        </label>

        <PrimaryButton loading={loading} testid="register-submit">
          Create Account <ArrowRight className="h-4 w-4" />
        </PrimaryButton>
      </form>

      <div className="mt-6 pt-6 border-t border-slate-200 dark:border-white/10 text-center text-sm">
        <span className="text-slate-500 dark:text-[hsl(220,12%,60%)]">Already have an account? </span>
        <Link to="/login" className="text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] font-semibold hover:underline" data-testid="login-link">
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
};

/* ════════════════════════ Forgot Password ════════════════════════ */
export const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.requestPasswordReset(email);
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      setSubmitted(true);
      toast.success('If an account exists with this email, you will receive reset instructions.');
    }
    setLoading(false);
  };

  return (
    <AuthShell
      testid="forgot-password-page"
      eyebrow="RECOVERY"
      title="Forgot it?"
      italicAccent="We've got you."
      subtitle="Enter your email and we'll send a secure, one-time reset link. Links expire in one hour for safety."
    >
      <div className="mb-6" data-testid="forgot-password-card">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-2">◢ RESET PASSWORD</div>
        <h2 className="text-2xl font-bold tracking-[-0.02em]">{submitted ? 'Check your inbox.' : 'Reset your password.'}</h2>
        <p className="text-sm text-slate-500 dark:text-[hsl(220,12%,65%)] mt-1">
          {submitted ? 'A reset link is on its way (if an account exists).' : "We'll email you a secure reset link."}
        </p>
      </div>

      {submitted ? (
        <div className="space-y-6">
          <div className="text-center py-2">
            <div className="w-14 h-14 rounded-2xl bg-[hsl(226,100%,55%)]/10 border border-[hsl(226,100%,55%)]/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-6 w-6 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" />
            </div>
            <p className="text-sm text-slate-600 dark:text-[hsl(220,12%,70%)]">
              If an account exists for <span className="font-mono font-bold text-slate-900 dark:text-white">{email}</span>,
              you'll receive a reset link shortly.
            </p>
          </div>
          <Link to="/login" className="inline-flex w-full items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 bg-white dark:bg-white/[0.04] text-slate-900 dark:text-white font-semibold tracking-tight text-sm transition-colors" data-testid="back-to-login">
            <ArrowLeft className="h-4 w-4" /> Back to Login
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <LabelRow>Email Address</LabelRow>
            <FieldWrap icon={Mail}>
              <input
                id="email"
                type="email"
                required
                placeholder="you@firm.legal"
                className={inputClasses}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
              />
            </FieldWrap>
          </div>

          <PrimaryButton loading={loading} testid="forgot-submit">
            Send Reset Link <ArrowRight className="h-4 w-4" />
          </PrimaryButton>

          <div className="text-center pt-2">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.14em] uppercase text-slate-500 dark:text-[hsl(220,12%,60%)] hover:text-[hsl(226,100%,52%)] dark:hover:text-[hsl(226,100%,68%)] transition-colors">
              <ArrowLeft className="h-3 w-3" /> Back to Login
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

/* ════════════════════════ Reset Password ════════════════════════ */
export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ password: '', confirmPassword: '' });

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!token) { toast.error('Invalid reset link'); return; }

    setLoading(true);
    try {
      await api.resetPassword(token, form.password);
      toast.success('Password reset successfully!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <AuthShell
        testid="reset-password-page"
        eyebrow="LINK EXPIRED"
        title="That link is"
        italicAccent="no longer valid."
        subtitle="Reset links expire after one hour. Request a fresh one to continue safely."
      >
        <div className="text-center py-2">
          <p className="text-sm text-slate-600 dark:text-[hsl(220,12%,70%)] mb-4">Invalid or expired reset link.</p>
          <Link to="/forgot-password" className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] text-white font-semibold tracking-tight text-sm shadow-[0_12px_36px_-10px_hsl(226,100%,55%)] transition-colors">
            Request New Reset Link <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testid="reset-password-page"
      eyebrow="NEW PASSWORD"
      title="Pick something"
      italicAccent="memorable & strong."
      subtitle="Use a passphrase you'll remember. We hash everything — even we can't see it."
    >
      <div className="mb-6" data-testid="reset-password-card">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-2">◢ RESET PASSWORD</div>
        <h2 className="text-2xl font-bold tracking-[-0.02em]">Set a new password.</h2>
        <p className="text-sm text-slate-500 dark:text-[hsl(220,12%,65%)] mt-1">Minimum 8 characters.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <LabelRow>New Password</LabelRow>
          <PasswordField
            id="password"
            value={form.password}
            onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder="Min 8 characters"
            testId="reset-password"
            autoComplete="new-password"
          />
        </div>
        <div>
          <LabelRow>Confirm New Password</LabelRow>
          <PasswordField
            id="confirmPassword"
            value={form.confirmPassword}
            onChange={(e) => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
            placeholder="Repeat new password"
            testId="reset-confirm-password"
            autoComplete="new-password"
          />
        </div>

        <PrimaryButton loading={loading} testid="reset-submit">
          Reset Password <ArrowRight className="h-4 w-4" />
        </PrimaryButton>
      </form>
    </AuthShell>
  );
};
